import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Spinner } from '@aigovops/design-system';
import {
  createRiskScenario,
  getLedgerPriors,
  listRiskScenarios,
  runRiskScenario,
  type RiskScenarioRow,
} from '../lib/api';
import { fmtDate } from '../lib/format';
import { useAnnounce } from './today/shared';

/**
 * V111-4-4 (ADR-0093, D-111-7): FAIR-lite loss exposure on the register item.
 * Two numbers lead — a typical year (median) and a bad year (1 in 20, p95) —
 * with where the calibration came from. Ranges, never a point estimate.
 */

const box: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-card)',
  background: 'var(--surface)',
  padding: 'var(--space-4)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const money = (n: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0, notation: n >= 1_000_000 ? 'compact' : 'standard' }).format(n);

export function LossExposureSection({
  workspaceSlug, registerItemId, canManage,
}: { workspaceSlug: string; registerItemId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const key = ['risk-scenarios', workspaceSlug, registerItemId] as const;
  const scenarios = useQuery({ queryKey: key, queryFn: () => listRiskScenarios(workspaceSlug, registerItemId) });
  const priors = useQuery({ queryKey: ['risk-priors', workspaceSlug], queryFn: () => getLedgerPriors(workspaceSlug), enabled: canManage });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [live, announce] = useAnnounce();
  const refresh = () => void qc.invalidateQueries({ queryKey: key });

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setFormOpen(false);
      announce('Loss estimate updated.');
      setTimeout(() => document.getElementById('loss-exposure-h')?.focus(), 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      // A scenario created before a failed run shows up with "Run estimate", so a retry never duplicates it.
      refresh();
      setBusy(false);
    }
  }

  const list = scenarios.data?.scenarios ?? [];
  const ledger = priors.data?.priors ?? null;

  return (
    <section style={box} aria-labelledby="loss-exposure-h">
      {live}
      <h2 id="loss-exposure-h" tabIndex={-1} style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>Loss exposure</h2>
      {scenarios.isLoading ? <Spinner label="Loading loss exposure" /> : null}
      {error ? <Alert variant="error">{error}</Alert> : null}

      {list.map((s) => (
        <ScenarioCard key={s.scenario_id} s={s} canManage={canManage} busy={busy}
          onRun={() => act(() => runRiskScenario(workspaceSlug, s.scenario_id))} />
      ))}

      {!scenarios.isLoading && list.length === 0 ? (
        canManage ? (
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
              No estimate yet. Underwriters ask what this risk could cost in a typical and a bad year.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {ledger ? (
                <Button variant="primary" compact disabled={busy}
                  onClick={() => act(async () => {
                    const { scenario } = await createRiskScenario(workspaceSlug, { register_item_id: registerItemId, title: 'Annual loss exposure', use_ledger_priors: true });
                    await runRiskScenario(workspaceSlug, scenario.scenario_id);
                  })}>
                  Estimate from your incident history ({ledger.basis.incident_count} incidents)
                </Button>
              ) : null}
              {!formOpen ? (
                <Button id="fair-enter-btn" variant={ledger ? 'ghost' : 'primary'} compact disabled={busy}
                  onClick={() => { setFormOpen(true); setTimeout(() => document.getElementById('fair-freqLow')?.focus(), 0); }}>
                  Enter an estimate
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
            No estimate yet. An owner, admin or editor can add one.
          </p>
        )
      ) : null}

      {formOpen ? (
        <ManualForm busy={busy} onCancel={() => { setFormOpen(false); setTimeout(() => (document.getElementById('fair-enter-btn') ?? document.getElementById('loss-exposure-h'))?.focus(), 0); }}
          onSubmit={(v) => act(async () => {
            const { scenario } = await createRiskScenario(workspaceSlug, { register_item_id: registerItemId, title: 'Annual loss exposure', ...v });
            await runRiskScenario(workspaceSlug, scenario.scenario_id);
          })} />
      ) : null}
    </section>
  );
}

function ScenarioCard({ s, canManage, busy, onRun }: { s: RiskScenarioRow; canManage: boolean; busy: boolean; onRun: () => void }) {
  const run = s.latest_run ?? null;
  const basis = (() => {
    try { return s.prior_basis_json ? (JSON.parse(s.prior_basis_json) as { incident_count?: number }) : null; } catch { return null; }
  })();
  const source = s.prior_source === 'ledger'
    ? `From your incident history (${basis?.incident_count ?? '—'} incidents)`
    : 'Estimated by your team';
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      {run ? (
        <div style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))' }}>
          <Figure label="Typical year" value={money(run.p50)} hint="median" />
          <Figure label="Bad year" value={money(run.p95)} hint="1 in 20 years" />
        </div>
      ) : (
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>Calibrated, not yet run.</p>
      )}
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
        <span>{source}</span>
        {run ? <span>Run {fmtDate(run.run_at)}</span> : null}
        {canManage ? (
          <Button variant="secondary" compact disabled={busy} onClick={onRun}>{run ? 'Run again' : 'Run estimate'}</Button>
        ) : null}
      </div>
    </div>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>{label} <span aria-hidden="true">·</span> {hint}</span>
      <span style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function ManualForm({ busy, onCancel, onSubmit }: {
  busy: boolean; onCancel: () => void;
  onSubmit: (v: Record<string, number>) => void;
}) {
  const [f, setF] = useState({ freqLow: '0.5', freqMost: '1', freqHigh: '3', magLow: '10000', magMost: '50000', magHigh: '250000' });
  const [err, setErr] = useState<string | null>(null);
  const num = (k: keyof typeof f) => Number(f[k]);
  function submit() {
    const v = {
      frequency_low: num('freqLow'), frequency_most_likely: num('freqMost'), frequency_high: num('freqHigh'),
      magnitude_low: num('magLow'), magnitude_most_likely: num('magMost'), magnitude_high: num('magHigh'),
    };
    if (Object.values(v).some((x) => !Number.isFinite(x) || x < 0)) return setErr('Use numbers of zero or more.');
    if (!(v.frequency_low <= v.frequency_most_likely && v.frequency_most_likely <= v.frequency_high && v.frequency_low < v.frequency_high)
      || !(v.magnitude_low <= v.magnitude_most_likely && v.magnitude_most_likely <= v.magnitude_high && v.magnitude_low < v.magnitude_high)) {
      return setErr('Each range must run low ≤ likely ≤ high.');
    }
    setErr(null);
    onSubmit(v);
  }
  const cell = (k: keyof typeof f, label: string) => (
    <Field id={`fair-${k}`} label={label}>
      <Input id={`fair-${k}`} inputMode="decimal" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </Field>
  );
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
        <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>How often per year</legend>
        <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          {cell('freqLow', 'Low')}{cell('freqMost', 'Likely')}{cell('freqHigh', 'High')}
        </div>
      </fieldset>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
        <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>Cost each time (USD)</legend>
        <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          {cell('magLow', 'Low')}{cell('magMost', 'Likely')}{cell('magHigh', 'High')}
        </div>
      </fieldset>
      {err ? <Alert variant="error">{err}</Alert> : null}
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button variant="primary" compact disabled={busy} onClick={submit}>{busy ? 'Estimating…' : 'Estimate'}</Button>
        <Button variant="ghost" compact disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
